import React from 'react';
import { useUserPlan } from '@/hooks/useUserPlan';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import AppLayout from '@/components/AppLayout';

interface ProFeatureGateProps {
  children: React.ReactNode;
  feature: string;
  featureLabel?: string;
}

export const ProFeatureGate: React.FC<ProFeatureGateProps> = ({
  children,
  feature,
  featureLabel,
}) => {
  const { canAccessFeature, loading } = useUserPlan();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Not logged in — let the page handle its own auth redirect
  if (!user) return <>{children}</>;

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </AppLayout>
    );
  }

  if (canAccessFeature(feature)) {
    return <>{children}</>;
  }

  // Blocked — show upgrade prompt
  return (
    <AppLayout>
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card className="max-w-lg w-full border-primary/20 shadow-xl">
          <CardContent className="flex flex-col items-center text-center gap-6 py-10">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground">
                Función exclusiva PRO
              </h2>
              <p className="text-muted-foreground">
                <strong>{featureLabel || feature}</strong> está disponible en el Plan PRO.
                Actualiza tu plan para desbloquear esta y muchas más funcionalidades avanzadas.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 w-full text-left space-y-2 text-sm">
              <p className="font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" /> Plan PRO incluye:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li>Proyectos y usuarios ilimitados</li>
                <li>IA Predictiva completa</li>
                <li>Reportes ejecutivos con IA</li>
                <li>OKRs, Sprints y automatizaciones</li>
                <li>Orquestador de agentes</li>
                <li>Integraciones avanzadas</li>
              </ul>
            </div>

            <Button
              size="lg"
              onClick={() => navigate('/settings?tab=billing')}
              className="gap-2"
            >
              Actualizar a PRO — $5 USD/mes
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};
